"use client"

import * as React from "react"
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { usePageScrollBlock } from "@/hooks/use-page-scroll-block"
import { XIcon } from "lucide-react"

const DialogDepthContext = React.createContext(0)
const DialogOpenContext = React.createContext<boolean | undefined>(undefined)

function useDialogDepth() {
  return React.useContext(DialogDepthContext)
}

function useFrozenWhileClosed<T>(value: T): T {
  const open = React.useContext(DialogOpenContext)
  const lastOpenValue = React.useRef(value)

  if (open !== false) {
    lastOpenValue.current = value
  }

  return open === false ? lastOpenValue.current : value
}

function DialogShellProvider({
  open,
  children,
}: {
  open?: boolean
  children: React.ReactNode
}) {
  const depth = useDialogDepth()
  return (
    <DialogDepthContext.Provider value={depth + 1}>
      <DialogOpenContext.Provider value={open}>
        {children}
      </DialogOpenContext.Provider>
    </DialogDepthContext.Provider>
  )
}

function Dialog({ modal = "trap-focus", ...props }: DialogPrimitive.Root.Props) {
  return (
    <DialogShellProvider open={props.open}>
      <DialogPrimitive.Root data-slot="dialog" modal={modal} {...props} />
    </DialogShellProvider>
  )
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  forceRender = true,
  ...props
}: DialogPrimitive.Backdrop.Props) {
  const nested = useDialogDepth() > 1
  usePageScrollBlock()

  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      data-nested={nested ? "" : undefined}
      forceRender={forceRender}
      className={cn(
        "fixed inset-0 isolate z-50 bg-black/10 supports-backdrop-filter:backdrop-blur-xs duration-100 data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0",
        className
      )}
      {...props}
    />
  )
}

function DialogCenterViewport({ children }: { children: React.ReactNode }) {
  return (
    <div className="pointer-events-none fixed inset-0 z-50 grid place-items-center">
      {children}
    </div>
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean
}) {
  const frozenClassName = useFrozenWhileClosed(className)

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogCenterViewport>
        <DialogPrimitive.Popup
          data-slot="dialog-content"
          className={cn(
            "pointer-events-auto relative grid w-full max-w-[min(24rem,calc(100%-30px))] gap-3 rounded-xl bg-popover p-3 text-sm text-popover-foreground ring-1 ring-foreground/10 duration-100 outline-none data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
            frozenClassName
          )}
          {...props}
        >
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close
              data-slot="dialog-close"
              render={
                <Button
                  variant="ghost"
                  className="absolute top-2 right-2"
                  size="icon-sm"
                />
              }
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Popup>
      </DialogCenterViewport>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2", className)}
      {...props}
    />
  )
}

function DialogTitleBar({
  title,
  subtitle,
  className,
  ...props
}: Omit<React.ComponentProps<"div">, "title"> & {
  title: React.ReactNode
  subtitle?: React.ReactNode
}) {
  return (
    <DialogHeader
      className={cn(
        "shrink-0 gap-0.5 border-b border-[#efefef] px-4 py-3.5 pr-11 dark:border-white/10",
        className
      )}
      {...props}
    >
      <DialogTitle className="text-[0.9375rem] leading-snug font-medium">
        {title}
      </DialogTitle>
      {subtitle ? (
        <div className="text-xs leading-snug text-muted-foreground">
          {subtitle}
        </div>
      ) : null}
    </DialogHeader>
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "-mx-3 -mb-3 flex flex-col-reverse gap-2 rounded-b-xl border-t bg-muted/50 px-3 py-2.5 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>
          Close
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "font-heading text-base leading-none font-medium",
        className
      )}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground",
        className
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogCenterViewport,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogShellProvider,
  DialogTitle,
  DialogTitleBar,
  DialogTrigger,
  useDialogDepth,
  useFrozenWhileClosed,
}
